if (!customElements.get('sticky-product-bar')) {
  customElements.define('sticky-product-bar', class extends HTMLElement {
    connectedCallback() {
      const start = () => this.init();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
      else start();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.unsubscribe?.();
      this.unsubscribeQty?.();
      this.releaseScroll?.();
      this.buyWatch?.disconnect();
      document.body.style.overflow = '';
    }

    init() {
      if (this.ready) return;
      this.ready = true;
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.select = this.querySelector('[data-sticky-variant]');
      this.input = this.querySelector('[name="id"]');
      this.button = this.querySelector('[type="submit"]');
      this.price = this.querySelector('[data-sticky-price]');
      this.compare = this.querySelector('[data-sticky-compare]');
      this.variantLabel = this.querySelector('[data-sticky-variant-label]');
      this.variantTrigger = this.querySelector('[data-sticky-variant-trigger]');
      this.sheet = this.querySelector('[data-sticky-sheet]');
      this.panel = this.querySelector('[data-sticky-panel]');
      this.image = this.querySelector('.sticky-product-bar__image');
      this.stickyQty = this.querySelector('[data-sticky-qty]');
      this.main = document.querySelector('product-info[id^="MainProduct"]');
      this.mainQty = this.main?.querySelector('.product-form__quantity input.quantity__input');
      this.qtyLock = false;

      if (this.stickyQty && this.mainQty) this.stickyQty.value = this.mainQty.value;
      this.stickyQty?.addEventListener('change', () => this.pushQty(this.stickyQty.value, this.mainQty));
      this.mainQty?.addEventListener('change', () => this.pushQty(this.mainQty.value, this.stickyQty));

      this.select?.addEventListener('change', () => {
        const variant = this.variants[this.select.value];
        if (!variant) return;
        this.paint(this.select.value, variant);
        this.syncMain(variant);
      });
      this.bindSheet();

      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data?.variant || data.sectionId !== this.main?.dataset.section) return;
          const id = String(data.variant.id);
          if (this.select && this.select.value !== id) this.select.value = id;
          if (this.variants[id]) this.paint(id, this.variants[id]);
          if (this.holdingScroll) {
            this.holdingScroll = false;
            clearTimeout(this.scrollHoldTimer);
            this.scrollHoldTimer = setTimeout(() => this.releaseScroll?.(), 800);
          }
        });
        this.unsubscribeQty = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
          this.mainQty = this.main?.querySelector('.product-form__quantity input.quantity__input');
          if (this.mainQty) this.pushQty(this.mainQty.value, this.stickyQty);
        });
      }

      this.watchBuyAnchor();
      this.place();
      window.addEventListener('resize', () => {
        this.bindBuyObserver();
        this.place();
      }, { passive: true });
    }

    buyLabels() {
      return [
        this.button?.dataset.labelAdd,
        window.variantStrings?.addToCart,
        'In den Warenkorb',
        'Add to cart',
        'Add To Cart',
      ]
        .filter(Boolean)
        .map((label) => label.toLowerCase());
    }

    isOnPage(el) {
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 || rect.height > 0;
    }

    buyAnchor() {
      const root = this.main;
      const fallback = root?.querySelector('.product-form__submit');
      if (!root) return fallback || null;

      const labels = this.buyLabels();
      const nodes = root.querySelectorAll('button, [role="button"], input[type="submit"]');
      const matches = [];
      nodes.forEach((el) => {
        if (this.contains(el) || !this.isOnPage(el)) return;
        const text = (el.innerText || el.value || '').replace(/\s+/g, ' ').trim().toLowerCase();
        if (!text) return;
        if (labels.some((label) => text.includes(label))) matches.push(el);
      });

      if (!matches.length) return fallback || null;
      return matches.reduce((lowest, el) => {
        const top = el.getBoundingClientRect().top + window.scrollY;
        const lowestTop = lowest.getBoundingClientRect().top + window.scrollY;
        return top >= lowestTop ? el : lowest;
      });
    }

    bindBuyObserver() {
      const buyButton = this.buyAnchor();
      if (buyButton === this.observedBuy) return;
      this.observer?.disconnect();
      this.observedBuy = buyButton;
      if (!buyButton || typeof IntersectionObserver === 'undefined') {
        this.hidden = true;
        return;
      }
      this.observer = new IntersectionObserver(([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.hidden = !scrolledPast;
        this.place();
      }, { threshold: 0 });
      this.observer.observe(buyButton);
    }

    watchBuyAnchor() {
      this.bindBuyObserver();
      if (!this.main) return;
      this.buyWatch = new MutationObserver(() => {
        clearTimeout(this.buyWatchTimer);
        this.buyWatchTimer = setTimeout(() => this.bindBuyObserver(), 200);
      });
      this.buyWatch.observe(this.main, { childList: true, subtree: true });
      window.addEventListener('load', () => this.bindBuyObserver(), { once: true });
      [400, 1200, 2500].forEach((ms) => setTimeout(() => this.bindBuyObserver(), ms));
    }

    pushQty(value, target) {
      if (this.qtyLock || !target || target.value === value) return;
      this.qtyLock = true;
      target.value = value;
      target.dispatchEvent(new Event('change', { bubbles: true }));
      this.qtyLock = false;
    }

    paint(id, variant) {
      this.input.value = id;
      this.price.textContent = variant.price;
      this.compare.hidden = !variant.compare;
      this.compare.textContent = variant.compare || '';
      if (variant.image && this.image && this.image.tagName === 'IMG') this.image.src = variant.image;
      if (this.variantLabel) this.variantLabel.textContent = variant.title || '';
      this.querySelectorAll('[data-sticky-pick]').forEach((btn) => {
        btn.classList.toggle('is-selected', btn.dataset.stickyPick === String(id));
      });
      if (this.stickyQty) {
        this.stickyQty.min = variant.min || 1;
        this.stickyQty.step = variant.step || 1;
        this.stickyQty.dataset.min = this.stickyQty.min;
        if (variant.max) {
          this.stickyQty.max = variant.max;
          this.stickyQty.dataset.max = variant.max;
        } else {
          this.stickyQty.removeAttribute('max');
          delete this.stickyQty.dataset.max;
        }
        if (Number(this.stickyQty.value) < Number(this.stickyQty.min)) {
          this.stickyQty.value = this.stickyQty.min;
          this.pushQty(this.stickyQty.value, this.mainQty);
        }
      }
      const label = variant.available ? this.button.dataset.labelAdd : this.button.dataset.labelNotify;
      if (label) this.button.querySelector('span').textContent = label;
      this.button.classList.toggle('is-notify', !variant.available);
      this.button.toggleAttribute('disabled', !variant.available);
      this.button.toggleAttribute('aria-disabled', !variant.available);
      if (!variant.available) this.button.setAttribute('aria-disabled', 'true');
    }

    bindSheet() {
      if (!this.sheet || !this.panel) return;
      this.variantTrigger?.addEventListener('click', () => this.openSheet());
      this.sheet.querySelectorAll('[data-sticky-sheet-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeSheet());
      });
      this.sheet.querySelectorAll('[data-sticky-pick]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.stickyPick;
          const variant = this.variants[id];
          if (!variant) return;
          if (this.select) this.select.value = id;
          this.paint(id, variant);
          this.syncMain(variant);
          this.closeSheet();
        });
      });
      this.bindSwipe();
    }

    openSheet() {
      this.sheet.hidden = false;
      requestAnimationFrame(() => this.sheet.classList.add('is-open'));
      document.body.style.overflow = 'hidden';
    }

    closeSheet() {
      this.sheet.classList.remove('is-open');
      this.panel.style.transform = '';
      document.body.style.overflow = '';
      const hide = () => { this.sheet.hidden = true; };
      this.panel.addEventListener('transitionend', hide, { once: true });
      setTimeout(hide, 320);
    }

    bindSwipe() {
      let startY = 0;
      let moveY = 0;
      let dragging = false;
      this.panel.addEventListener('touchstart', (event) => {
        startY = event.touches[0].clientY;
        moveY = 0;
        dragging = true;
        this.panel.style.transition = 'none';
      }, { passive: true });
      this.panel.addEventListener('touchmove', (event) => {
        if (!dragging) return;
        moveY = event.touches[0].clientY - startY;
        if (moveY < 0) moveY = 0;
        this.panel.style.transform = `translateY(${moveY}px)`;
      }, { passive: true });
      const endDrag = () => {
        if (!dragging) return;
        dragging = false;
        this.panel.style.transition = '';
        if (moveY > 80) this.closeSheet();
        else this.panel.style.transform = '';
      };
      this.panel.addEventListener('touchend', endDrag);
      this.panel.addEventListener('touchcancel', endDrag);
    }

    holdScroll() {
      this.releaseScroll?.();
      const y = window.scrollY;
      const root = document.documentElement;
      const previousAnchor = root.style.overflowAnchor;
      const previousBodyAnchor = document.body.style.overflowAnchor;
      root.setAttribute('data-sticky-variant', '');
      root.style.overflowAnchor = 'none';
      document.body.style.overflowAnchor = 'none';
      let active = true;
      let correcting = false;
      const hold = () => {
        if (!active || correcting || Math.abs(window.scrollY - y) < 2) return;
        correcting = true;
        window.scrollTo(0, y);
        requestAnimationFrame(() => { correcting = false; });
      };
      window.addEventListener('scroll', hold, { passive: true });
      this.holdingScroll = true;
      this.releaseScroll = () => {
        if (!active) return;
        active = false;
        this.holdingScroll = false;
        window.removeEventListener('scroll', hold);
        root.style.overflowAnchor = previousAnchor;
        document.body.style.overflowAnchor = previousBodyAnchor;
        root.removeAttribute('data-sticky-variant');
        if (Math.abs(window.scrollY - y) > 2) window.scrollTo(0, y);
      };
      clearTimeout(this.scrollHoldTimer);
      this.scrollHoldTimer = setTimeout(() => this.releaseScroll?.(), 2500);
    }

    syncMain(variant) {
      const picker = this.main?.querySelector('variant-selects');
      if (!picker) return;
      this.holdScroll();
      const groups = picker.querySelectorAll(':scope > fieldset, :scope > .product-form__input');
      let control = null;
      variant.options.forEach((value, index) => {
        const group = groups[index];
        if (!group) return;
        const radio = [...group.querySelectorAll('input[type="radio"]')].find((el) => el.value === value);
        if (radio) {
          radio.checked = true;
          control = radio;
          return;
        }
        const select = group.querySelector('select');
        const option = select && [...select.options].find((el) => el.value === value);
        if (!option) return;
        [...select.options].forEach((el) => el.toggleAttribute('selected', el === option));
        select.value = value;
        control = select;
      });
      control?.dispatchEvent(new Event('change', { bubbles: true }));
    }

    place() {
      const mobileBar = document.querySelector('.sticky_bottombar');
      const lift = mobileBar && getComputedStyle(mobileBar).display !== 'none' ? mobileBar.offsetHeight : 0;
      this.style.bottom = lift + 'px';
    }
  });
}
